import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  GripVertical,
  FileText,
  Video,
  BookOpen,
  Upload,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Calendar,
  Sparkles,
  RefreshCw,
  Eye,
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import ActivityContentBuilder from '../../components/admin/ActivityContentBuilder';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { WeekNavigator, CollapsedWeekSummary, weekColor } from '../../components/admin/WeekNavigator';
import ProgrammeRubricTab from '../../components/admin/ProgrammeRubricTab';
import ProgrammeOutlineTab from '../../components/admin/ProgrammeOutlineTab';
import AssignmentEditor from '../../components/admin/AssignmentEditor';
import StudyNotesTab from '../../components/admin/StudyNotesTab';
import ModuleCard from '../../components/admin/ModuleCard';
import UnsavedChangesDialog from '../../components/admin/UnsavedChangesDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import ProgrammeThumbnailCard from '../../components/programme/ProgrammeThumbnailCard';

const API = `${API_BASE}/api`;

const ACTIVITY_TYPES = [
  { value: 'ai_course', label: 'AI Course (NAVI Generated)', icon: Sparkles, hasContentBuilder: true, isAI: true },
  { value: 'course', label: 'Course (Build Manually)', icon: BookOpen, hasContentBuilder: true },
  { value: 'existing_course', label: 'Course (Select Existing)', icon: BookOpen, hasContentBuilder: false },
  { value: 'existing_lab', label: 'Learning Lab (Select Existing)', icon: BookOpen, hasContentBuilder: true },
  { value: 'video', label: 'Video', icon: Video, hasContentBuilder: false },
  { value: 'masterclass', label: 'Masterclass (Select Existing)', icon: Video, hasContentBuilder: false },
  { value: 'aicc', label: 'AICC / External', icon: ExternalLink, hasContentBuilder: false },
  { value: 'document', label: 'Document', icon: FileText, hasContentBuilder: false },
  { value: 'assignment_brief', label: 'Assignment Brief', icon: Upload, hasContentBuilder: false },
  { value: 'assignment', label: 'Assignment', icon: Upload, hasContentBuilder: false },
];

export default function ProgrammeBuilderPage() {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const { token } = useAuth();
  const isEditing = !!programmeId;

  const headers = { Authorization: `Bearer ${token}` };

  // Programme metadata
  const [metadata, setMetadata] = useState({
    programme_name: '',
    professional_body: '',
    designation: '',
    occupation: '',
    nqf_level: '',
    target_audience: '',
    entry_requirements: '',
    duration: '',
    credits: '',
    facilitator_name: '',
    moderator_name: '',
    start_date: '',
    end_date: '',
    structure_type: 'weeks',
    description: '',
    instructions_html: '',
    is_published: false
  });

  // Periods (weeks/months) with activities
  const [periods, setPeriods] = useState([]);
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [activePeriodId, setActivePeriodId] = useState(null);
  const [modules, setModules] = useState([]);
  const [collapsedModules, setCollapsedModules] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('structure');
  const periodUnit = metadata.structure_type === 'months' ? 'Month' : 'Week';
  const [atRiskCount, setAtRiskCount] = useState(0);

  useEffect(() => {
    if (!programmeId) return;
    axios.get(`${API}/programmes/${programmeId}/rubric/scores`, { headers })
      .then((r) => setAtRiskCount(r.data.at_risk_count || 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId]);
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify({ metadata, periods: [] }));
  const dirty = useMemo(() => JSON.stringify({ metadata, periods }) !== savedSnapshot, [metadata, periods, savedSnapshot]);
  const { pending: leavePending, guard, confirmLeave, cancelLeave, disable: disableGuard } = useUnsavedChangesGuard(dirty, navigate);

  // Content selection for activities
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableLabs, setAvailableLabs] = useState([]);
  const [availableMasterclasses, setAvailableMasterclasses] = useState([]);

  useEffect(() => {
    fetchAvailableContent();
    if (isEditing) {
      fetchProgramme();
    }
  }, [programmeId]);

  const fetchAvailableContent = async () => {
    try {
      const [coursesRes, labsRes, masterclassesRes] = await Promise.all([
        axios.get(`${API}/courses`, { headers }),
        axios.get(`${API}/labs?limit=200`, { headers }),
        axios.get(`${API}/webinars`, { headers })
      ]);
      setAvailableCourses(coursesRes.data.courses || coursesRes.data || []);
      setAvailableLabs(labsRes.data.labs || labsRes.data || []);
      setAvailableMasterclasses(masterclassesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  const fetchProgramme = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}`, { headers });
      const programme = res.data;
      setThumbnailUrl(programme.thumbnail_url || null);
      
      const loadedMeta = {
        programme_name: programme.programme_name || '',
        professional_body: programme.professional_body || '',
        designation: programme.designation || '',
        occupation: programme.occupation || '',
        nqf_level: programme.nqf_level || '',
        target_audience: programme.target_audience || '',
        entry_requirements: programme.entry_requirements || '',
        duration: programme.duration || '',
        credits: programme.credits || '',
        facilitator_name: programme.facilitator_name || '',
        moderator_name: programme.moderator_name || '',
        start_date: programme.start_date || '',
        end_date: programme.end_date || '',
        structure_type: programme.structure_type || 'weeks',
        description: programme.description || '',
        instructions_html: programme.instructions_html || '',
        is_published: programme.is_published || false
      };
      setMetadata(loadedMeta);
      setPeriods(programme.periods || []);
      setSavedSnapshot(JSON.stringify({ metadata: loadedMeta, periods: programme.periods || [] }));
      
      // Collapse all periods except the first (keep any the admin already opened)
      setExpandedPeriods(prev => {
        const expanded = {};
        (programme.periods || []).forEach((p, i) => {
          expanded[p.period_id] = prev[p.period_id] ?? i === 0;
        });
        return expanded;
      });
    } catch (error) {
      toast.error('Failed to load programme');
      navigate('/programmes');
    } finally {
      setLoading(false);
    }
  };

  const [labSearch, setLabSearch] = useState('');
  const filteredLabs = useMemo(() => {
    const q = labSearch.trim().toLowerCase();
    if (!q) return availableLabs;
    return availableLabs.filter(l => [l.title, l.topic, l.industry, l.level, l.description]
      .some(v => String(v || '').toLowerCase().includes(q)));
  }, [availableLabs, labSearch]);

  const refreshLabs = async () => {
    try {
      const res = await axios.get(`${API}/labs?limit=200`, { headers });
      setAvailableLabs(res.data.labs || []);
      toast.success('Lab list refreshed');
    } catch (e) {
      toast.error('Failed to refresh labs');
    }
  };

  const handleMetadataChange = (field, value) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProgramme = async (periodsOverride) => {
    const periodsToSave = Array.isArray(periodsOverride) ? periodsOverride : periods;
    setSaving(true);
    try {
      let currentProgrammeId = programmeId;

      if (isEditing) {
        // Update existing programme
        await axios.put(`${API}/programmes/${programmeId}`, metadata, { headers });
      } else {
        // Create new programme
        const res = await axios.post(`${API}/programmes`, metadata, { headers });
        currentProgrammeId = res.data.programme_id;
      }

      // Save periods and activities (create new, update existing)
      const buildActivityPayload = (activity) => ({
        title: activity.title,
        activity_type: activity.activity_type,
        content_id: activity.content_id,
        external_url: activity.external_url,
        document_url: activity.document_url,
        description: activity.description,
        order: activity.order,
        is_required: activity.is_required,
        allows_poe_submission: activity.allows_poe_submission,
        content_type: activity.content_type,
        ai_generated: activity.ai_generated,
        ai_course_id: activity.ai_course_id,
        ai_lab_id: activity.ai_lab_id,
        ...(activity.activity_type === 'assignment' ? { assignment: activity.assignment || {}, due_at: activity.due_at || null, allows_poe_submission: true } : {}),
      });

      for (const [periodIndex, period] of periodsToSave.entries()) {
        let periodId = period.period_id;
        const periodPayload = { period_number: periodIndex + 1, title: period.title, description: period.description || '', module_id: period.module_id || '' };

        if (period.isNew) {
          // Create new period
          const periodRes = await axios.post(`${API}/programmes/${currentProgrammeId}/periods`, periodPayload, { headers });
          periodId = periodRes.data.period_id;
        } else {
          await axios.put(`${API}/programmes/${currentProgrammeId}/periods/${periodId}`, periodPayload, { headers });
        }

        // Create new activities and update existing ones
        for (const activity of period.activities || []) {
          const isTemp = !activity.activity_id || String(activity.activity_id).startsWith('temp_');
          if (isTemp) {
            await axios.post(
              `${API}/programmes/${currentProgrammeId}/periods/${periodId}/activities`,
              buildActivityPayload(activity),
              { headers }
            );
          } else {
            // Update existing activity (status is owned by publish/unpublish endpoints)
            await axios.put(
              `${API}/programmes/${currentProgrammeId}/activities/${activity.activity_id}`,
              buildActivityPayload(activity),
              { headers }
            );
          }
        }
      }

      toast.success(isEditing ? 'Programme saved' : 'Programme created');
      if (isEditing) {
        // Refresh so temp IDs are replaced with real ones (prevents duplicates)
        await fetchProgramme();
      } else {
        disableGuard();
        window.location.href = `/admin/programmes/${currentProgrammeId}/edit`;
      }
    } catch (error) {
      console.error('Save error:', error);
      const detail = error?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : detail;
      toast.error(msg ? `Failed to save programme: ${msg}` : 'Failed to save programme');
      return false;
    } finally {
      setSaving(false);
    }
    return true;
  };

  const addPeriod = (moduleIdArg) => {
    const newPeriodNumber = periods.length + 1;
    const structureLabel = metadata.structure_type === 'weeks' ? 'Week' : 'Month';
    const targetModule = typeof moduleIdArg === 'string' ? moduleIdArg : modules[modules.length - 1]?.module_id || null;
    const newPeriod = {
      period_id: `temp_${Date.now()}`,
      period_number: newPeriodNumber,
      title: `${structureLabel} ${newPeriodNumber}`,
      description: '',
      module_id: targetModule,
      activities: [],
      isNew: true
    };
    setPeriods(sortByModule([...periods, newPeriod]));
    setExpandedPeriods(prev => ({ ...prev, [newPeriod.period_id]: true }));
    setTimeout(() => jumpToPeriod(newPeriod.period_id), 60);
  };

  const removePeriod = async (periodId, isNew) => {
    if (!window.confirm('Delete this period and all its activities?')) return;

    if (!isNew && programmeId) {
      try {
        await axios.delete(`${API}/programmes/${programmeId}/periods/${periodId}`, { headers });
      } catch (error) {
        toast.error('Failed to delete period');
        return;
      }
    }

    setPeriods(periods.filter(p => p.period_id !== periodId));
    toast.success('Period deleted');
  };

  const updatePeriod = (periodId, field, value) => {
    setPeriods(periods.map(p => 
      p.period_id === periodId ? { ...p, [field]: value } : p
    ));
  };

  const addActivity = (periodId) => {
    setPeriods(periods.map(p => {
      if (p.period_id === periodId) {
        const newActivity = {
          activity_id: `temp_${Date.now()}`,
          title: '',
          activity_type: 'course',
          content_id: '',
          external_url: '',
          document_url: '',
          description: '',
          order: (p.activities?.length || 0) + 1,
          is_required: true,
          allows_poe_submission: false,
          isNew: true
        };
        return { ...p, activities: [...(p.activities || []), newActivity] };
      }
      return p;
    }));
  };

  const removeActivity = async (periodId, activityId, isNew) => {
    if (!isNew && programmeId) {
      try {
        await axios.delete(`${API}/programmes/${programmeId}/activities/${activityId}`, { headers });
      } catch (error) {
        toast.error('Failed to delete activity');
        return;
      }
    }

    setPeriods(periods.map(p => {
      if (p.period_id === periodId) {
        return { ...p, activities: p.activities.filter(a => a.activity_id !== activityId) };
      }
      return p;
    }));
  };

  const updateActivity = (periodId, activityId, field, value) => {
    setPeriods(periods.map(p => {
      if (p.period_id === periodId) {
        return {
          ...p,
          activities: p.activities.map(a => 
            a.activity_id === activityId ? { ...a, [field]: value } : a
          )
        };
      }
      return p;
    }));
  };

  const togglePeriod = (periodId) => {
    setExpandedPeriods(prev => ({ ...prev, [periodId]: !prev[periodId] }));
  };

  const loadModules = async () => {
    if (!programmeId) return;
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/modules`, { headers });
      const mods = res.data.modules || [];
      setModules(mods);
      if (Object.keys(res.data.synced || {}).length) fetchProgramme();
    } catch (e) {
      console.warn('Failed to load modules', e);
    }
  };

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, savedSnapshot]);

  const moduleIndex = useMemo(() => Object.fromEntries(modules.map((m, i) => [m.module_id, i])), [modules]);
  const periodColor = (period, idx) => (period.module_id && moduleIndex[period.module_id] !== undefined ? weekColor(moduleIndex[period.module_id]) : weekColor(idx));
  const sortByModule = (list, index = moduleIndex) => {
    const rank = (id) => (id && index[id] !== undefined ? index[id] : 9999);
    return list.map((p, i) => ({ p, i })).sort((a, b) => rank(a.p.module_id) - rank(b.p.module_id) || a.i - b.i).map((x) => x.p);
  };
  const moduleGroups = useMemo(() => {
    const withIndex = periods.map((period, index) => ({ period, index }));
    const groups = modules.map((m) => ({ module: m, periods: withIndex.filter((x) => x.period.module_id === m.module_id) }));
    const unassigned = withIndex.filter((x) => !x.period.module_id || moduleIndex[x.period.module_id] === undefined);
    if (unassigned.length || modules.length === 0) groups.push({ module: null, periods: unassigned });
    return groups;
  }, [periods, modules, moduleIndex]);

  const assignModule = (periodId, moduleId) => {
    setPeriods((prev) => sortByModule(prev.map((p) => (p.period_id === periodId ? { ...p, module_id: moduleId } : p))));
    setTimeout(() => jumpToPeriod(periodId), 80);
  };

  const addModule = async (assignPeriodId) => {
    if (!programmeId) {
      toast.info('Save the programme first, then add modules');
      return;
    }
    try {
      const res = await axios.post(`${API}/programmes/${programmeId}/modules`, {}, { headers });
      const next = [...modules, { ...res.data, week_count: 0, activity_count: 0, published_count: 0 }];
      setModules(next);
      if (typeof assignPeriodId === 'string') {
        const index = Object.fromEntries(next.map((m, i) => [m.module_id, i]));
        setPeriods((prev) => sortByModule(prev.map((p) => (p.period_id === assignPeriodId ? { ...p, module_id: res.data.module_id } : p)), index));
        setTimeout(() => jumpToPeriod(assignPeriodId), 80);
        toast.success(`${res.data.title} created — click Save Programme to keep the ${periodUnit.toLowerCase()} in it`);
      } else {
        toast.success(`${res.data.title} added — add ${periodUnit.toLowerCase()}s to it or move existing ones with the Module dropdown`);
        setTimeout(() => document.querySelector(`[data-testid="module-card-${res.data.module_id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
      }
      loadModules();
    } catch (e) {
      toast.error('Failed to add module');
    }
  };

  const deleteModule = async (module) => {
    if (!window.confirm(`Delete "${module.title}"? Its ${periodUnit.toLowerCase()}s and activities are kept and become unassigned.`)) return;
    try {
      await axios.delete(`${API}/programmes/${programmeId}/modules/${module.module_id}`, { headers });
      setPeriods((prev) => prev.map((p) => (p.module_id === module.module_id ? { ...p, module_id: null } : p)));
      await loadModules();
      toast.success('Module deleted');
    } catch (e) {
      toast.error('Failed to delete module');
    }
  };

  const moveModule = async (idx, dir) => {
    const next = [...modules];
    const [m] = next.splice(idx, 1);
    next.splice(idx + dir, 0, m);
    setModules(next);
    setPeriods((prev) => sortByModule(prev, Object.fromEntries(next.map((x, i) => [x.module_id, i]))));
    try {
      await axios.post(`${API}/programmes/${programmeId}/modules/reorder`, { module_ids: next.map((x) => x.module_id) }, { headers });
      toast.info(`Module order changed — click Save Programme to renumber ${periodUnit.toLowerCase()}s`);
    } catch (e) {
      toast.error('Failed to reorder modules');
    }
  };

  const publishModule = async (module, publish) => {
    const count = publish ? (module.activity_count || 0) - (module.published_count || 0) : module.published_count || 0;
    const msg = publish
      ? `Publish ${count} draft activit${count === 1 ? 'y' : 'ies'} in "${module.title}"? Linked learning labs will be published too.`
      : `Switch ${count} published activit${count === 1 ? 'y' : 'ies'} in "${module.title}" back to draft? Learners will no longer see them.`;
    if (!window.confirm(dirty ? `${msg}\n\nYour unsaved changes will be saved first.` : msg)) return;
    if (dirty && !(await handleSaveProgramme())) return;
    try {
      const res = await axios.post(`${API}/programmes/${programmeId}/modules/${module.module_id}/publish`, { publish }, { headers });
      toast.success(publish ? `${res.data.changed} activities published${res.data.labs_published ? ` · ${res.data.labs_published} labs published` : ''}${res.data.study_notes_status === 'generating' ? ' · writing study notes' : ''}` : `${res.data.changed} activities moved to draft`);
      await fetchProgramme();
      await loadModules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update module');
    }
  };

  const setAllExpanded = (open) => setExpandedPeriods(Object.fromEntries(periods.map(p => [p.period_id, open])));

  const jumpToPeriod = (periodId) => {
    setExpandedPeriods(prev => ({ ...prev, [periodId]: true }));
    setActivePeriodId(periodId);
    requestAnimationFrame(() => {
      const el = document.getElementById(`period-${periodId}`);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 140, behavior: 'smooth' });
    });
  };

  const periodIdsKey = periods.map(p => p.period_id).join(',');
  useEffect(() => {
    if (activeTab !== 'structure' || !periodIdsKey) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActivePeriodId(visible[0].target.dataset.periodId);
    }, { rootMargin: '-140px 0px -55% 0px' });
    periodIdsKey.split(',').forEach(id => {
      const el = document.getElementById(`period-${id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [periodIdsKey, activeTab]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-1/3" />
            <div className="h-64 bg-zinc-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => guard(() => navigate('/admin?tab=programmes'))}
              className="text-zinc-400 hover:text-white"
              data-testid="programme-builder-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {isEditing ? 'Edit Programme' : 'Create Programme'}
              </h1>
              <p className="text-sm text-zinc-400">
                {isEditing ? 'Update programme details and structure' : 'Build a new professional programme'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {programmeId && (
              <Button variant="outline" size="sm" onClick={() => window.open(`/programmes/${programmeId}`, '_blank')} className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10" title="Opens the programme exactly as an enrolled learner sees it (your admin preview enrolment)" data-testid="view-as-learner-btn">
                <Eye className="h-4 w-4 mr-1.5" />View as learner
              </Button>
            )}
            <Badge data-testid="programme-dirty-indicator" className={dirty ? 'bg-amber-500/15 text-amber-300' : 'bg-green-500/15 text-green-400'}>
              {dirty ? 'Unsaved changes' : 'All changes saved'}
            </Badge>
            <div className="flex items-center gap-2">
              <Switch
                checked={metadata.is_published}
                onCheckedChange={(checked) => handleMetadataChange('is_published', checked)}
                data-testid="programme-catalog-switch"
              />
              <Label className="text-sm text-zinc-400" title="Lists this programme in the Programmes catalog. Learners' access is controlled by publishing modules/activities.">Show in Programmes catalog</Label>
            </div>
            <Button 
              onClick={handleSaveProgramme}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="save-programme-top-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Programme'}
            </Button>
          </div>
        </div>

        <div className="mb-6" data-testid="edit-page-thumbnail">
          <ProgrammeThumbnailCard key={`${programmeId || 'new'}-${thumbnailUrl || ''}`} programmeId={programmeId} thumbnailUrl={thumbnailUrl} headers={headers} onChange={setThumbnailUrl} />
        </div>

        {/* Programme Metadata */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white">Programme Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Row 1 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Programme Name</Label>
                <Input
                  value={metadata.programme_name}
                  onChange={(e) => handleMetadataChange('programme_name', e.target.value)}
                  placeholder="e.g., Advanced Compliance Professional"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Professional Body</Label>
                <Input
                  value={metadata.professional_body}
                  onChange={(e) => handleMetadataChange('professional_body', e.target.value)}
                  placeholder="e.g., CISI, ICA, SAICA"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400">Designation</Label>
                <Input
                  value={metadata.designation}
                  onChange={(e) => handleMetadataChange('designation', e.target.value)}
                  placeholder="e.g., Certified Compliance Officer"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Occupation</Label>
                <Input
                  value={metadata.occupation}
                  onChange={(e) => handleMetadataChange('occupation', e.target.value)}
                  placeholder="e.g., Compliance Manager"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">NQF Level</Label>
                <Input
                  value={metadata.nqf_level}
                  onChange={(e) => handleMetadataChange('nqf_level', e.target.value)}
                  placeholder="e.g., 5, 6, 7"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Target Audience</Label>
                <Input
                  value={metadata.target_audience}
                  onChange={(e) => handleMetadataChange('target_audience', e.target.value)}
                  placeholder="e.g., Compliance professionals, Risk managers"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Entry Requirements</Label>
                <Input
                  value={metadata.entry_requirements}
                  onChange={(e) => handleMetadataChange('entry_requirements', e.target.value)}
                  placeholder="e.g., Bachelor's degree or 3 years experience"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 4 */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-zinc-400">Duration</Label>
                <Input
                  value={metadata.duration}
                  onChange={(e) => handleMetadataChange('duration', e.target.value)}
                  placeholder="e.g., 12 weeks"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Credits</Label>
                <Input
                  value={metadata.credits}
                  onChange={(e) => handleMetadataChange('credits', e.target.value)}
                  placeholder="e.g., 120"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Facilitator Name</Label>
                <Input
                  value={metadata.facilitator_name}
                  onChange={(e) => handleMetadataChange('facilitator_name', e.target.value)}
                  placeholder="Facilitator name"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Moderator Name</Label>
                <Input
                  value={metadata.moderator_name}
                  onChange={(e) => handleMetadataChange('moderator_name', e.target.value)}
                  placeholder="Moderator name"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            {/* Row 5 - Dates and Structure */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400">Start Date</Label>
                <Input
                  type="date"
                  value={metadata.start_date}
                  onChange={(e) => handleMetadataChange('start_date', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">End Date</Label>
                <Input
                  type="date"
                  value={metadata.end_date}
                  onChange={(e) => handleMetadataChange('end_date', e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Structure Type</Label>
                <Select
                  value={metadata.structure_type}
                  onValueChange={(value) => handleMetadataChange('structure_type', value)}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="weeks">Weekly Structure</SelectItem>
                    <SelectItem value="months">Monthly Structure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={metadata.description}
                onChange={(e) => handleMetadataChange('description', e.target.value)}
                placeholder="Programme description..."
                rows={3}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6" data-testid="programme-builder-tabs">
            <TabsTrigger value="structure" data-testid="tab-structure">Programme Structure</TabsTrigger>
            <TabsTrigger value="description" data-testid="tab-description">Description</TabsTrigger>
            <TabsTrigger value="rubric" data-testid="tab-rubric" className="gap-2">
              Assessment Rubric
              {atRiskCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white" data-testid="tab-rubric-at-risk-badge" title="At-risk learners">
                  {atRiskCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="study-guides" data-testid="tab-study-guides">Study Notes</TabsTrigger>
            <TabsTrigger value="outline" data-testid="tab-outline">Course Outline</TabsTrigger>
          </TabsList>

          <TabsContent value="outline">
            {programmeId ? (
              <ProgrammeOutlineTab programmeId={programmeId} token={token} />
            ) : (
              <Card className="bg-zinc-900 border-zinc-800 border-dashed" data-testid="outline-save-first">
                <CardContent className="py-12 text-center text-zinc-400">Save the programme first, then generate the learner course outline.</CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="description" data-testid="programme-description-tab">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Learner Instructions</CardTitle>
                <p className="text-sm text-zinc-400">Shown to enrolled learners at the top of the programme page. Use headings, lists and links to guide them.</p>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  value={metadata.instructions_html}
                  onChange={(html) => handleMetadataChange('instructions_html', html)}
                  placeholder="e.g. Welcome to the programme. Complete each week in order, then upload your final integrated assignment..."
                />
                <p className="text-xs text-zinc-500 mt-3">Click Save Programme to publish these instructions.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="study-guides">
            {programmeId ? (
              <StudyNotesTab programmeId={programmeId} token={token} />
            ) : (
              <Card className="bg-zinc-900 border-zinc-800 border-dashed" data-testid="study-guides-save-first">
                <CardContent className="py-12 text-center text-zinc-400">Save the programme first, then generate study notes for each module.</CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="rubric">
            {programmeId ? (
              <ProgrammeRubricTab
                programmeId={programmeId}
                token={token}
                refreshKey={`${activeTab}-${savedSnapshot.length}`}
                onAtRiskCount={setAtRiskCount}
              />
            ) : (
              <Card className="bg-zinc-900 border-zinc-800 border-dashed" data-testid="rubric-save-first">
                <CardContent className="py-12 text-center text-zinc-400">Save the programme first — the rubric is created automatically from its activities.</CardContent>
              </Card>
            )}
          </TabsContent>

        <TabsContent value="structure">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              Programme Structure ({metadata.structure_type === 'weeks' ? 'Weeks' : 'Months'})
            </h2>
            <Button 
              onClick={addPeriod}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add {metadata.structure_type === 'weeks' ? 'Week' : 'Month'}
            </Button>
          </div>

          {periods.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800 border-dashed">
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">
                  No {metadata.structure_type} added yet. Start building your programme structure.
                </p>
                <Button 
                  onClick={addPeriod}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First {metadata.structure_type === 'weeks' ? 'Week' : 'Month'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-[230px_minmax(0,1fr)] gap-6">
            <WeekNavigator
              periods={periods}
              groups={moduleGroups.map((g, gi) => ({ key: g.module?.module_id || 'unassigned', module: g.module, color: g.module ? weekColor(gi) : '#52525b', periods: g.periods }))}
              colorFor={periodColor}
              unit={periodUnit}
              activeId={activePeriodId}
              onJump={jumpToPeriod}
              onExpandAll={() => setAllExpanded(true)}
              onCollapseAll={() => setAllExpanded(false)}
              onAdd={addPeriod}
              onAddModule={programmeId ? () => addModule() : undefined}
            />
            <div className="space-y-5 min-w-0">
              {modules.length > 0 || programmeId ? (
                <div className="flex justify-end">
                  {programmeId && (
                    <Button type="button" size="sm" variant="outline" onClick={addModule} className="border-zinc-700 text-zinc-200" data-testid="add-module-btn">
                      <Plus className="h-4 w-4 mr-1.5" />Add Module
                    </Button>
                  )}
                </div>
              ) : null}
              {moduleGroups.map((group, groupIndex) => {
                const renderWeeks = () => group.periods.map(({ period, index: periodIndex }) => (
                <Card
                  key={period.period_id}
                  id={`period-${period.period_id}`}
                  data-period-id={period.period_id}
                  className="bg-zinc-900 border-zinc-800 border-l-4 scroll-mt-36"
                  style={{ borderLeftColor: periodColor(period, periodIndex) }}
                  data-testid={`period-card-${period.period_id}`}
                >
                  <Collapsible
                    open={expandedPeriods[period.period_id]}
                    onOpenChange={() => togglePeriod(period.period_id)}
                  >
                    <CollapsibleTrigger asChild>
                      <CardHeader
                        className={`cursor-pointer hover:bg-zinc-800/60 transition-colors rounded-t-xl ${expandedPeriods[period.period_id] ? 'sticky top-[7.25rem] lg:top-16 z-20 bg-zinc-900/95 backdrop-blur border-b border-zinc-800' : ''}`}
                        data-testid={`period-header-${period.period_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 min-w-0">
                            <GripVertical className="h-5 w-5 text-zinc-600 shrink-0" />
                            <span className="h-8 w-8 shrink-0 rounded-md text-sm font-bold flex items-center justify-center text-black" style={{ backgroundColor: periodColor(period, periodIndex) }}>
                              {periodIndex + 1}
                            </span>
                            <div className="min-w-0">
                              <CardTitle className="text-white text-lg truncate">
                                {period.title || `${periodUnit} ${periodIndex + 1}`}
                              </CardTitle>
                              <p className="text-sm text-zinc-400 truncate">
                                {periodUnit} {periodIndex + 1} · {period.activities?.length || 0} activities
                                {period.description ? ` · ${period.description}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePeriod(period.period_id, period.isNew);
                              }}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {expandedPeriods[period.period_id] ? (
                              <ChevronUp className="h-5 w-5 text-zinc-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-zinc-400" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    {!expandedPeriods[period.period_id] && (
                      <CollapsedWeekSummary period={period} color={periodColor(period, periodIndex)} />
                    )}

                    <CollapsibleContent>
                      <CardContent className="pt-4 space-y-4">
                        {/* Period Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-zinc-800/50 rounded-lg">
                          {modules.length > 0 && (
                            <div className="md:col-span-2">
                              <Label className="text-zinc-400 text-sm">Module</Label>
                              <Select value={period.module_id || 'none'} onValueChange={(v) => (v === '__new__' ? addModule(period.period_id) : assignModule(period.period_id, v === 'none' ? null : v))}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 md:max-w-sm" data-testid={`period-module-select-${period.period_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                  {modules.map((m) => <SelectItem key={m.module_id} value={m.module_id}>{m.title}</SelectItem>)}
                                  <SelectItem value="none">No module</SelectItem>
                                  <SelectItem value="__new__" className="text-teal-300" data-testid={`period-module-new-${period.period_id}`}>+ New module ({`Module ${modules.length + 1}`})</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <div>
                            <Label className="text-zinc-400 text-sm">Period Title</Label>
                            <Input
                              value={period.title}
                              onChange={(e) => updatePeriod(period.period_id, 'title', e.target.value)}
                              placeholder={`${metadata.structure_type === 'weeks' ? 'Week' : 'Month'} ${period.period_number}`}
                              className="bg-zinc-800 border-zinc-700 text-white mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-sm">
                              {metadata.structure_type === 'months' ? 'Month' : 'Week'} description (shown to learners)
                            </Label>
                            <Textarea
                              value={period.description || ''}
                              onChange={(e) => updatePeriod(period.period_id, 'description', e.target.value)}
                              placeholder={`What learners will focus on this ${metadata.structure_type === 'months' ? 'month' : 'week'}`}
                              rows={2}
                              maxLength={600}
                              className="bg-zinc-800 border-zinc-700 text-white mt-1 resize-y min-h-[40px]"
                              data-testid={`period-description-${period.period_id}`}
                            />
                          </div>
                        </div>

                        {/* Activities */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-zinc-300 font-medium">Activities</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => addActivity(period.period_id)}
                              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Activity
                            </Button>
                          </div>

                          {/* Created activities summary — confirms what exists in this period */}
                          {period.activities && period.activities.length > 0 && (
                            <div className="mb-4 p-3 bg-zinc-900/60 rounded-lg border border-zinc-800">
                              <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wide">Created Activities ({period.activities.length})</p>
                              <div className="space-y-1.5">
                                {period.activities.map((a, idx) => {
                                  const persisted = a.activity_id && !String(a.activity_id).startsWith('temp_');
                                  const published = (a.status || 'draft') === 'published';
                                  const typeLabel = (ACTIVITY_TYPES.find(t => t.value === a.activity_type)?.label) || a.activity_type;
                                  return (
                                    <div key={a.activity_id || idx} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`activity-summary-${idx}`}>
                                      <span className="text-white font-medium truncate max-w-full md:max-w-[40%]">{a.title || 'Untitled activity'}</span>
                                      <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">{typeLabel}</Badge>
                                      <Badge className={published ? 'text-[10px] bg-green-500/20 text-green-400' : 'text-[10px] bg-amber-500/20 text-amber-400'}>
                                        {published ? 'Published' : 'Draft'}
                                      </Badge>
                                      <Badge className={persisted ? 'text-[10px] bg-green-500/15 text-green-400' : 'text-[10px] bg-zinc-600/30 text-zinc-300'}>
                                        {persisted ? 'Saved' : 'Unsaved'}
                                      </Badge>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {(!period.activities || period.activities.length === 0) ? (
                            <div className="text-center py-6 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
                              <p className="text-sm text-zinc-500">No activities yet</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {period.activities.map((activity, activityIndex) => {
                                const activityType = ACTIVITY_TYPES.find(t => t.value === activity.activity_type);
                                const hasContentBuilder = activityType?.hasContentBuilder || false;
                                
                                return (
                                <div 
                                  key={activity.activity_id}
                                  className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 border-l-4"
                                  style={{ borderLeftColor: periodColor(period, periodIndex) }}
                                  data-testid={`activity-card-${activity.activity_id}`}
                                >
                                  <div className="flex items-center gap-2 mb-3">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: periodColor(period, periodIndex), backgroundColor: `${periodColor(period, periodIndex)}1f` }} data-testid={`activity-week-tag-${activity.activity_id}`}>
                                      {periodUnit} {periodIndex + 1}
                                    </span>
                                    <span className="text-[11px] text-zinc-500">Activity {activityIndex + 1} of {period.activities.length}</span>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
                                    {/* Activity Type */}
                                    <div className="col-span-2 md:col-span-3">
                                      <Label className="text-zinc-400 text-xs">Type</Label>
                                      <Select
                                        value={activity.activity_type}
                                        onValueChange={(value) => updateActivity(period.period_id, activity.activity_id, 'activity_type', value)}
                                      >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-800 border-zinc-700">
                                          {ACTIVITY_TYPES.map(type => (
                                            <SelectItem key={type.value} value={type.value}>
                                              {type.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {/* Activity Title */}
                                    <div className="col-span-2 md:col-span-4">
                                      <Label className="text-zinc-400 text-xs">Title</Label>
                                      <Input
                                        value={activity.title}
                                        onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'title', e.target.value)}
                                        placeholder="Activity title"
                                        className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                      />
                                    </div>

                                    {/* Content Selection or URL - Only for non-builder types */}
                                    <div className="col-span-2 md:col-span-3">
                                      {['existing_course', 'masterclass'].includes(activity.activity_type) ? (
                                        <>
                                          <Label className="text-zinc-400 text-xs">Select Content</Label>
                                          <Select
                                            value={activity.content_id || ''}
                                            onValueChange={(value) => updateActivity(period.period_id, activity.activity_id, 'content_id', value)}
                                          >
                                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9">
                                              <SelectValue placeholder="Select..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-800 border-zinc-700 max-h-48">
                                              {activity.activity_type === 'existing_course' && availableCourses.map(c => (
                                                <SelectItem key={c.course_id} value={c.course_id}>
                                                  {c.title}
                                                </SelectItem>
                                              ))}
                                              {activity.activity_type === 'existing_lab' && availableLabs.map(l => (
                                                <SelectItem key={l.lab_id} value={l.lab_id}>
                                                  {l.title || l.name}
                                                </SelectItem>
                                              ))}
                                              {activity.activity_type === 'masterclass' && availableMasterclasses.map(m => (
                                                <SelectItem key={m.webinar_id} value={m.webinar_id}>
                                                  {m.title}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </>
                                      ) : activity.activity_type === 'existing_lab' ? (
                                        <>
                                          <div className="flex items-center justify-between">
                                            <Label className="text-zinc-400 text-xs">Select Lab</Label>
                                            <span className="flex items-center gap-2">
                                              <a href="/admin?tab=labs" target="_blank" rel="noreferrer" className="text-[11px] text-teal-400 hover:text-teal-300 inline-flex items-center gap-0.5" data-testid="create-lab-in-admin-link">
                                                <Plus className="h-3 w-3" /> Create in Admin
                                              </a>
                                              <button type="button" onClick={refreshLabs} title="Refresh labs" className="text-zinc-400 hover:text-white" data-testid="refresh-labs-btn">
                                                <RefreshCw className="h-3 w-3" />
                                              </button>
                                            </span>
                                          </div>
                                          <Select
                                            value={activity.content_id || ''}
                                            onValueChange={(value) => {
                                              const lab = availableLabs.find(l => l.lab_id === value);
                                              setPeriods(prev => prev.map(p => p.period_id !== period.period_id ? p : {
                                                ...p,
                                                activities: p.activities.map(a => a.activity_id !== activity.activity_id ? a : {
                                                  ...a, content_id: value, content_type: 'learning_lab', title: a.title || lab?.title || ''
                                                })
                                              }));
                                            }}
                                          >
                                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9" data-testid="select-lab-trigger">
                                              <SelectValue placeholder="Select..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-800 border-zinc-700 max-h-48">
                                              <div className="p-1.5 sticky top-0 bg-zinc-800 z-10">
                                                <Input
                                                  value={labSearch}
                                                  onChange={(e) => setLabSearch(e.target.value)}
                                                  onKeyDown={(e) => e.stopPropagation()}
                                                  placeholder="Search labs..."
                                                  className="h-8 bg-zinc-900 border-zinc-700 text-white text-xs"
                                                  data-testid="lab-picker-search"
                                                />
                                              </div>
                                              {availableLabs.length === 0 && (
                                                <div className="px-2 py-1.5 text-xs text-zinc-500">No labs yet — create one in Admin → Learning Labs</div>
                                              )}
                                              {availableLabs.length > 0 && filteredLabs.length === 0 && (
                                                <div className="px-2 py-1.5 text-xs text-zinc-500" data-testid="lab-picker-no-results">No labs match "{labSearch}"</div>
                                              )}
                                              {(filteredLabs.some(l => l.lab_id === activity.content_id) ? filteredLabs : [...filteredLabs, ...availableLabs.filter(l => l.lab_id === activity.content_id)]).map(l => (
                                                <SelectItem key={l.lab_id} value={l.lab_id}>
                                                  {l.title || l.name}{l.status !== 'published' ? ' (draft)' : ''}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </>
                                      ) : activity.activity_type === 'aicc' ? (
                                        <>
                                          <Label className="text-zinc-400 text-xs">External/AICC URL (iframe embed)</Label>
                                          <Input
                                            value={activity.external_url || ''}
                                            onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'external_url', e.target.value)}
                                            placeholder="https://omo.oldmutualcompliance.net/?aicc_embed=..."
                                            className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                          />
                                          <p className="text-xs text-zinc-500 mt-1">This URL will be embedded in an iframe for learners</p>
                                        </>
                                      ) : activity.activity_type === 'video' || activity.activity_type === 'masterclass' ? (
                                        <>
                                          <Label className="text-zinc-400 text-xs">Video/Embed URL (iframe)</Label>
                                          <Input
                                            value={activity.external_url || ''}
                                            onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'external_url', e.target.value)}
                                            placeholder="YouTube/Vimeo/Embed URL"
                                            className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                          />
                                          <p className="text-xs text-zinc-500 mt-1">Video will be embedded in an iframe for learners</p>
                                        </>
                                      ) : ['document', 'assignment_brief', 'assignment'].includes(activity.activity_type) ? (
                                        <div className="space-y-2">
                                          <Label className="text-zinc-400 text-xs">
                                            {activity.activity_type === 'assignment' ? 'Optional brief attachment' : activity.activity_type === 'assignment_brief' ? 'Assignment Brief Document' : 'Document'} (PDF/DOCX)
                                          </Label>
                                          {activity.document_filename ? (
                                            <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded border border-zinc-700">
                                              <FileText className="h-4 w-4 text-teal-400" />
                                              <span className="text-sm text-white flex-1 truncate">{activity.document_filename}</span>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => updateActivity(period.period_id, activity.activity_id, 'document_filename', null)}
                                                className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ) : (
                                            <div className="relative">
                                              <input
                                                type="file"
                                                accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls"
                                                onChange={async (e) => {
                                                  const file = e.target.files?.[0];
                                                  if (file && programmeId) {
                                                    const formData = new FormData();
                                                    formData.append('file', file);
                                                    try {
                                                      const res = await axios.post(
                                                        `${API}/programmes/${programmeId}/activities/${activity.activity_id}/upload-document`,
                                                        formData,
                                                        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
                                                      );
                                                      updateActivity(period.period_id, activity.activity_id, 'document_filename', res.data.filename);
                                                      updateActivity(period.period_id, activity.activity_id, 'document_id', res.data.document_id);
                                                      toast.success('Document uploaded');
                                                    } catch (err) {
                                                      toast.error('Upload failed');
                                                    }
                                                  }
                                                }}
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                              />
                                              <div className="flex items-center gap-2 p-2 border border-dashed border-zinc-600 rounded hover:border-teal-500 cursor-pointer">
                                                <Upload className="h-4 w-4 text-zinc-400" />
                                                <span className="text-sm text-zinc-400">Click to upload document</span>
                                              </div>
                                            </div>
                                          )}
                                          <p className="text-xs text-zinc-500">
                                            {activity.activity_type === 'assignment_brief' 
                                              ? 'Learners will download this brief and submit their work as POE'
                                              : 'Learners can download this document'}
                                          </p>
                                        </div>
                                      ) : hasContentBuilder ? (
                                        <div className="flex items-end">
                                          <Badge className="bg-amber-500/20 text-amber-400 h-9 flex items-center">
                                            <Sparkles className="h-3 w-3 mr-1" />
                                            Build Content Below
                                          </Badge>
                                        </div>
                                      ) : (
                                        <>
                                          <Label className="text-zinc-400 text-xs">Description</Label>
                                          <Input
                                            value={activity.description || ''}
                                            onChange={(e) => updateActivity(period.period_id, activity.activity_id, 'description', e.target.value)}
                                            placeholder="Brief description"
                                            className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"
                                          />
                                        </>
                                      )}
                                    </div>

                                    {/* POE Toggle & Delete */}
                                    <div className="col-span-2 flex items-end gap-2">
                                      <div className="flex items-center gap-1">
                                        <Switch
                                          checked={activity.allows_poe_submission}
                                          onCheckedChange={(checked) => updateActivity(period.period_id, activity.activity_id, 'allows_poe_submission', checked)}
                                          className="scale-75"
                                        />
                                        <Label className="text-zinc-400 text-xs">POE</Label>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeActivity(period.period_id, activity.activity_id, activity.isNew)}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 w-9 p-0"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {activity.activity_type === 'assignment' && (
                                    <AssignmentEditor activity={activity} onChange={(field, value) => updateActivity(period.period_id, activity.activity_id, field, value)} />
                                  )}

                                  {/* Inline Content Builder for course/learning_lab types */}
                                  {hasContentBuilder && (
                                    <ActivityContentBuilder
                                      activity={activity}
                                      onUpdate={(updatedActivity) => {
                                        setPeriods(periods.map(p => {
                                          if (p.period_id === period.period_id) {
                                            return {
                                              ...p,
                                              activities: p.activities.map(a =>
                                                a.activity_id === activity.activity_id ? updatedActivity : a
                                              )
                                            };
                                          }
                                          return p;
                                        }));
                                      }}
                                      token={token}
                                      programmeId={programmeId}
                                      onAutoSave={async (updated) => {
                                        // Auto-save after generation so courses/labs don't disappear
                                        const nextPeriods = periods.map(p => p.period_id === period.period_id
                                          ? { ...p, activities: p.activities.map(a => a.activity_id === activity.activity_id ? updated : a) }
                                          : p);
                                        await handleSaveProgramme(nextPeriods);
                                      }}
                                    />
                                  )}
                                </div>
                              )})}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
                ));
                if (!group.module) {
                  return (
                    <div key="unassigned" className="space-y-5" data-testid="unassigned-weeks">
                      {modules.length > 0 && group.periods.length > 0 && (
                        <p className="text-xs uppercase tracking-[0.14em] text-zinc-500 pt-2">{periodUnit}s without a module</p>
                      )}
                      {renderWeeks()}
                    </div>
                  );
                }
                return (
                  <ModuleCard
                    key={group.module.module_id}
                    module={group.module}
                    color={weekColor(groupIndex)}
                    unit={periodUnit}
                    programmeId={programmeId}
                    token={token}
                    collapsed={!!collapsedModules[group.module.module_id]}
                    onToggle={() => setCollapsedModules((c) => ({ ...c, [group.module.module_id]: !c[group.module.module_id] }))}
                    onReload={loadModules}
                    onPublish={publishModule}
                    onDelete={deleteModule}
                    onMove={(dir) => moveModule(groupIndex, dir)}
                    isFirst={groupIndex === 0}
                    isLast={groupIndex === modules.length - 1}
                  >
                    {renderWeeks()}
                    <button
                      type="button"
                      onClick={() => addPeriod(group.module.module_id)}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-dashed border-zinc-700 text-sm text-zinc-400 hover:text-white hover:border-teal-500/60 transition-colors"
                      data-testid={`add-week-to-module-${group.module.module_id}`}
                    >
                      <Plus className="h-4 w-4" />Add {periodUnit.toLowerCase()} to {group.module.title}
                    </button>
                  </ModuleCard>
                );
              })}
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={addPeriod}
                  className="w-full flex items-center justify-center gap-2 py-5 rounded-xl border-2 border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-teal-500/60 hover:bg-teal-500/5 transition-colors"
                  data-testid="add-period-bottom-btn"
                >
                  <Plus className="h-4 w-4" />
                  Add {periodUnit} {periods.length + 1}
                </button>
                <button
                  type="button"
                  onClick={() => addModule()}
                  className="w-full flex items-center justify-center gap-2 py-5 rounded-xl border-2 border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-teal-500/60 hover:bg-teal-500/5 transition-colors"
                  data-testid="add-module-bottom-btn"
                >
                  <Plus className="h-4 w-4" />
                  Add Module {modules.length + 1}
                </button>
              </div>
            </div>
            </div>
          )}
        </div>
        </TabsContent>
        </Tabs>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end pt-6 border-t border-zinc-800">
          <Button 
            onClick={handleSaveProgramme}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="save-programme-bottom-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Programme'}
          </Button>
        </div>
      </div>
      <UnsavedChangesDialog
        open={leavePending}
        saving={saving}
        onCancel={cancelLeave}
        onLeave={confirmLeave}
        onSaveAndLeave={async () => { if (await handleSaveProgramme()) confirmLeave(); }}
      />
    </Layout>
  );
}
